import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../config/api';

/* ------------------------------------------------------------------ */
/*  Tiny helpers                                                       */
/* ------------------------------------------------------------------ */
const fmt = (val, currency = 'USD') =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
    }).format(val);

const statusColor = {
    draft: '#6c757d',
    sent: '#0d6efd',
    paid: '#198754',
    cancelled: '#dc3545',
};

const statusBadgeClass = {
    draft: 'badge-soft-secondary',
    sent: 'badge-soft-primary',
    paid: 'badge-soft-success',
    cancelled: 'badge-soft-danger',
};

const avatarColors = ['bg-teal', 'bg-warning', 'bg-orange', 'bg-success', 'bg-info', 'bg-indigo', 'bg-pink'];

const timeAgo = (isoStr) => {
    const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
};

const activityIcon = {
    draft: 'ti-pencil',
    sent: 'ti-send',
    paid: 'ti-cash',
    cancelled: 'ti-x',
};

const activityBg = {
    draft: 'bg-info',
    sent: 'bg-warning',
    paid: 'bg-success',
    cancelled: 'bg-danger',
};

/* ------------------------------------------------------------------ */
/*  Inline styles                                                      */
/* ------------------------------------------------------------------ */
const styles = {
    miniChart: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: '3px',
        height: '48px',
    },
    bar: (h, color) => ({
        width: '8px',
        height: `${h}%`,
        backgroundColor: color,
        borderRadius: '3px 3px 0 0',
        transition: 'height 0.6s ease',
        minHeight: '4px',
    }),
    donutContainer: {
        position: 'relative',
        width: '200px',
        height: '200px',
        margin: '0 auto 16px',
    },
    donutCenter: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        textAlign: 'center',
    },
    revenueBar: (pct, color) => ({
        height: '100%',
        width: `${pct}%`,
        backgroundColor: color,
        borderRadius: '4px',
        transition: 'width 0.8s ease',
        minWidth: pct > 0 ? '6px' : '0',
    }),
    revenueBarBg: {
        width: '100%',
        height: '28px',
        backgroundColor: '#f0f0f5',
        borderRadius: '4px',
        overflow: 'hidden',
    },
};

/* ------------------------------------------------------------------ */
/*  CSS-only Donut via conic-gradient                                  */
/* ------------------------------------------------------------------ */
const DonutChart = ({ paid, sent, draft, cancelled, total }) => {
    if (total === 0) {
        return (
            <div style={styles.donutContainer}>
                <svg viewBox="0 0 200 200" width="200" height="200">
                    <circle cx="100" cy="100" r="80" fill="none" stroke="#e9ecef" strokeWidth="30" />
                </svg>
                <div style={styles.donutCenter}>
                    <h4 className="fw-bold mb-0">0</h4>
                    <small className="text-muted">Invoices</small>
                </div>
            </div>
        );
    }
    const pPaid = (paid / total) * 100;
    const pSent = (sent / total) * 100;
    const pDraft = (draft / total) * 100;
    const pCancelled = (cancelled / total) * 100;

    const gradient = `conic-gradient(
        ${statusColor.paid} 0% ${pPaid}%,
        ${statusColor.sent} ${pPaid}% ${pPaid + pSent}%,
        ${statusColor.draft} ${pPaid + pSent}% ${pPaid + pSent + pDraft}%,
        ${statusColor.cancelled} ${pPaid + pSent + pDraft}% 100%
    )`;

    return (
        <div style={styles.donutContainer}>
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <div
                    style={{
                        width: '130px',
                        height: '130px',
                        borderRadius: '50%',
                        background: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <h4 className="fw-bold mb-0">{total}</h4>
                    <small className="text-muted">Invoices</small>
                </div>
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Mini Bar Chart (revenue overview)                                  */
/* ------------------------------------------------------------------ */
const MiniBarChart = ({ data }) => {
    const max = Math.max(...data.map((d) => d.revenue), 1);
    return (
        <div style={styles.miniChart}>
            {data.map((d, i) => (
                <div
                    key={i}
                    title={`${d.month}: ${fmt(d.revenue)}`}
                    style={styles.bar((d.revenue / max) * 100, '#7c5cfc')}
                />
            ))}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Main Stats component                                               */
/* ------------------------------------------------------------------ */
const Stats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStats = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/dashboard-stats/');
            setStats(res.data);
            setError(null);
        } catch (err) {
            console.error('Dashboard fetch error:', err);
            setError('Unable to load dashboard data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    /* Loading skeleton */
    if (loading) {
        return (
            <div className="page-wrapper">
                <div className="content pb-0">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                        <h4 className="fw-bold mb-0">Dashboard</h4>
                    </div>
                    <div className="row">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="col-md-6 col-xl-3 d-flex">
                                <div className="card flex-fill" style={{ minHeight: '140px' }}>
                                    <div className="card-body d-flex align-items-center justify-content-center">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading…</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    /* Error state */
    if (error) {
        return (
            <div className="page-wrapper">
                <div className="content pb-0">
                    <div className="alert alert-danger d-flex align-items-center" role="alert">
                        <i className="ti ti-alert-circle fs-20 me-2" />
                        <div>
                            {error}{' '}
                            <button className="btn btn-sm btn-outline-danger ms-2" onClick={fetchStats}>
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const {
        total_invoices,
        paid_count,
        sent_count,
        draft_count,
        cancelled_count,
        overdue_count,
        total_revenue,
        outstanding,
        month_revenue,
        revenue_change,
        monthly_data,
        total_clients,
        top_clients,
        recent_invoices,
    } = stats;

    const maxMonthRev = Math.max(...monthly_data.map((d) => d.revenue), 1);

    return (
        <>
            <div className="page-wrapper">
                <div className="content pb-0">
                    {/* Header */}
                    <div className="d-flex align-items-center justify-content-between mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">Dashboard</h4>
                            <p className="text-muted mb-0 fs-13">
                                Overview of your invoicing activity
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-sm btn-outline-white border"
                                onClick={fetchStats}
                                title="Refresh"
                            >
                                <i className="ti ti-refresh me-1" />
                                Refresh
                            </button>
                            <Link to="/create-invoice" className="btn btn-sm btn-primary">
                                <i className="ti ti-plus me-1" />
                                New Invoice
                            </Link>
                        </div>
                    </div>

                    {/* ─── KPI Cards ─── */}
                    <div className="row">
                        {/* Total Invoices */}
                        <div className="col-md-6 col-xl-3 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div className="avatar avtar-lg bg-teal mb-2">
                                                <i className="ti ti-file-invoice fs-24 text-white" />
                                            </div>
                                            <h6 className="fs-14 fw-semibold mb-1">Total Invoices</h6>
                                            <h3 className="fw-bold mb-0">{total_invoices}</h3>
                                        </div>
                                        <MiniBarChart data={monthly_data} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Clients */}
                        <div className="col-md-6 col-xl-3 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div className="avatar avtar-lg bg-warning mb-2">
                                                <i className="ti ti-users fs-24 text-white" />
                                            </div>
                                            <h6 className="fs-14 fw-semibold mb-1">Clients</h6>
                                            <h3 className="fw-bold mb-0">{total_clients}</h3>
                                        </div>
                                        <Link
                                            to="/clients"
                                            className="btn btn-sm btn-outline-warning"
                                        >
                                            View All
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Overdue */}
                        <div className="col-md-6 col-xl-3 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div className="avatar avtar-lg bg-orange mb-2">
                                                <i className="ti ti-alert-triangle fs-24 text-white" />
                                            </div>
                                            <h6 className="fs-14 fw-semibold mb-1">Overdue</h6>
                                            <h3 className="fw-bold mb-0">{overdue_count}</h3>
                                        </div>
                                        <Link
                                            to="/invoices"
                                            className="btn btn-sm btn-outline-danger"
                                        >
                                            Review
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Revenue */}
                        <div className="col-md-6 col-xl-3 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div className="avatar avtar-lg bg-teal mb-2">
                                                <i className="ti ti-currency-dollar fs-24 text-white" />
                                            </div>
                                            <h6 className="fs-14 fw-semibold mb-1">Revenue</h6>
                                            <h3 className="fw-bold mb-0">{fmt(total_revenue)}</h3>
                                        </div>
                                        <div className="text-end">
                                            <span
                                                className={`badge ${
                                                    revenue_change >= 0
                                                        ? 'badge-soft-success'
                                                        : 'badge-soft-danger'
                                                }`}
                                            >
                                                <i
                                                    className={`ti ${
                                                        revenue_change >= 0
                                                            ? 'ti-trending-up'
                                                            : 'ti-trending-down'
                                                    } me-1`}
                                                />
                                                {revenue_change >= 0 ? '+' : ''}
                                                {revenue_change}%
                                            </span>
                                            <p className="fs-12 text-muted mb-0 mt-1">vs last month</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* end KPI row */}

                    {/* ─── Summary Cards Row ─── */}
                    <div className="row">
                        <div className="col-md-10 col-xl-4 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <p className="mb-1 text-dark">This Month</p>
                                            <h6 className="fs-16 fw-semibold mb-1">{fmt(month_revenue)}</h6>
                                        </div>
                                        <span
                                            className={`badge ${
                                                revenue_change >= 0 ? 'badge-soft-success' : 'badge-soft-danger'
                                            }`}
                                        >
                                            <i
                                                className={`ti ${
                                                    revenue_change >= 0
                                                        ? 'ti-trending-up'
                                                        : 'ti-trending-down'
                                                } me-1`}
                                            />
                                            {revenue_change >= 0 ? '+' : ''}
                                            {revenue_change}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 col-xl-4 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <p className="mb-1 text-dark">Paid Invoices</p>
                                            <h6 className="fs-16 fw-semibold mb-1">{paid_count}</h6>
                                        </div>
                                        <span className="badge badge-soft-success">
                                            {total_invoices > 0
                                                ? Math.round((paid_count / total_invoices) * 100)
                                                : 0}
                                            %
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 col-xl-4 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <p className="mb-1 text-dark">Outstanding</p>
                                            <h6 className="fs-16 fw-semibold mb-1">{fmt(outstanding)}</h6>
                                        </div>
                                        <span className="badge badge-soft-warning">{sent_count} pending</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ─── Charts Row ─── */}
                    <div className="row">
                        {/* Donut: Invoice Status */}
                        <div className="col-md-4 col-xl-4 d-flex">
                            <div className="card flex-fill">
                                <div className="card-header">
                                    <h5 className="fw-bold mb-0">Invoice Status</h5>
                                </div>
                                <div className="card-body">
                                    <DonutChart
                                        paid={paid_count}
                                        sent={sent_count}
                                        draft={draft_count}
                                        cancelled={cancelled_count}
                                        total={total_invoices}
                                    />
                                    <div className="row mt-2">
                                        <div className="col-6">
                                            <h6 className="fs-14 mb-3">
                                                <i
                                                    className="ti ti-square-filled me-1"
                                                    style={{ color: statusColor.paid }}
                                                />
                                                Paid ({paid_count})
                                            </h6>
                                            <h6 className="fs-14 mb-0">
                                                <i
                                                    className="ti ti-square-filled me-1"
                                                    style={{ color: statusColor.sent }}
                                                />
                                                Sent ({sent_count})
                                            </h6>
                                        </div>
                                        <div className="col-6">
                                            <h6 className="fs-14 mb-3">
                                                <i
                                                    className="ti ti-square-filled me-1"
                                                    style={{ color: statusColor.draft }}
                                                />
                                                Draft ({draft_count})
                                            </h6>
                                            <h6 className="fs-14 mb-0">
                                                <i
                                                    className="ti ti-square-filled me-1"
                                                    style={{ color: statusColor.cancelled }}
                                                />
                                                Cancelled ({cancelled_count})
                                            </h6>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Revenue Overview (bar chart) */}
                        <div className="col-md-8 col-xl-5 d-flex">
                            <div className="card flex-fill">
                                <div className="card-header">
                                    <h5 className="fw-bold mb-0">Revenue Overview</h5>
                                </div>
                                <div className="card-body">
                                    {monthly_data.map((d, i) => (
                                        <div key={i} className="mb-3">
                                            <div className="d-flex justify-content-between mb-1">
                                                <span className="fs-13 text-dark">{d.month}</span>
                                                <span className="fs-13 fw-semibold">{fmt(d.revenue)}</span>
                                            </div>
                                            <div style={styles.revenueBarBg}>
                                                <div
                                                    style={styles.revenueBar(
                                                        (d.revenue / maxMonthRev) * 100,
                                                        '#7c5cfc'
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Quick stats sidebar */}
                        <div className="col-md-12 col-xl-3 d-flex">
                            <div className="card flex-fill">
                                <div className="card-header">
                                    <h6 className="fw-bold mb-0">Quick Stats</h6>
                                </div>
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between mb-3 pb-3 border-bottom">
                                        <div className="d-flex align-items-center">
                                            <div className="avatar avatar-sm bg-teal text-white d-flex align-items-center justify-content-center rounded-circle me-2">
                                                <i className="ti ti-file-invoice" />
                                            </div>
                                            <span className="fs-14">Total</span>
                                        </div>
                                        <span className="badge badge-md badge-soft-teal">{total_invoices}</span>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between mb-3 pb-3 border-bottom">
                                        <div className="d-flex align-items-center">
                                            <div className="avatar avatar-sm bg-success text-white d-flex align-items-center justify-content-center rounded-circle me-2">
                                                <i className="ti ti-check" />
                                            </div>
                                            <span className="fs-14">Paid</span>
                                        </div>
                                        <span className="badge badge-md badge-soft-success">{paid_count}</span>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between mb-3 pb-3 border-bottom">
                                        <div className="d-flex align-items-center">
                                            <div className="avatar avatar-sm bg-primary text-white d-flex align-items-center justify-content-center rounded-circle me-2">
                                                <i className="ti ti-send" />
                                            </div>
                                            <span className="fs-14">Sent</span>
                                        </div>
                                        <span className="badge badge-md badge-soft-primary">{sent_count}</span>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between mb-3 pb-3 border-bottom">
                                        <div className="d-flex align-items-center">
                                            <div className="avatar avatar-sm bg-secondary text-white d-flex align-items-center justify-content-center rounded-circle me-2">
                                                <i className="ti ti-pencil" />
                                            </div>
                                            <span className="fs-14">Draft</span>
                                        </div>
                                        <span className="badge badge-md badge-soft-secondary">{draft_count}</span>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <div className="avatar avatar-sm bg-danger text-white d-flex align-items-center justify-content-center rounded-circle me-2">
                                                <i className="ti ti-alert-triangle" />
                                            </div>
                                            <span className="fs-14">Overdue</span>
                                        </div>
                                        <span className="badge badge-md badge-soft-danger">{overdue_count}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* end charts row */}

                    {/* ─── Bottom Row: Activity + Top Clients ─── */}
                    <div className="row">
                        {/* Recent Invoice Activity */}
                        <div className="col-lg-5 d-flex">
                            <div className="card shadow flex-fill">
                                <div className="card-header d-flex align-items-center justify-content-between">
                                    <h6 className="fw-bold mb-0">Recent Invoice Activity</h6>
                                    <button
                                        className="btn btn-sm btn-icon btn-outline-white border-0"
                                        onClick={fetchStats}
                                        title="Refresh"
                                    >
                                        <i className="ti ti-refresh" />
                                    </button>
                                </div>
                                <div className="card-body">
                                    {recent_invoices.length === 0 ? (
                                        <p className="text-muted text-center py-4">No recent activity.</p>
                                    ) : (
                                        recent_invoices.map((inv, idx) => (
                                            <div
                                                key={inv.id}
                                                className={`d-flex align-items-center justify-content-between ${
                                                    idx < recent_invoices.length - 1 ? 'mb-3' : ''
                                                }`}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <span
                                                        className={`avatar avatar-sm avatar-rounded flex-shrink-0 ${
                                                            activityBg[inv.status] || 'bg-secondary'
                                                        } text-white d-flex align-items-center justify-content-center`}
                                                    >
                                                        <i className={`ti ${activityIcon[inv.status] || 'ti-file'}`} />
                                                    </span>
                                                    <div className="ms-2">
                                                        <h6 className="fs-14 mb-1">
                                                            <Link to={`/invoice-details/${inv.id}`}>
                                                                {inv.invoice_number}
                                                            </Link>
                                                        </h6>
                                                        <p className="fs-13 mb-0 text-truncate" style={{ maxWidth: '180px' }}>
                                                            {inv.client_name} — {fmt(inv.grand_total, inv.currency)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-end">
                                                    <span className={`badge ${statusBadgeClass[inv.status] || 'badge-soft-secondary'} mb-1`}>
                                                        {inv.status}
                                                    </span>
                                                    <p className="fs-12 text-muted mb-0">{timeAgo(inv.created_at)}</p>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Top Clients */}
                        <div className="col-lg-7 d-flex">
                            <div className="card shadow flex-fill">
                                <div className="card-header d-flex align-items-center justify-content-between">
                                    <h6 className="fw-bold mb-0">Top Clients</h6>
                                    <Link to="/clients" className="btn btn-sm btn-outline-white">
                                        View All Clients
                                    </Link>
                                </div>
                                <div className="card-body">
                                    {top_clients.length === 0 ? (
                                        <p className="text-muted text-center py-4">
                                            No client data yet. <Link to="/create-client">Add your first client</Link>.
                                        </p>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-nowrap border">
                                                <thead>
                                                    <tr>
                                                        <th>Client Name</th>
                                                        <th>Invoices</th>
                                                        <th>Total Billed</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {top_clients.map((c, i) => (
                                                        <tr key={c.id}>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <span
                                                                        className={`avatar avatar-sm avatar-rounded ${
                                                                            avatarColors[i % avatarColors.length]
                                                                        } text-white d-flex align-items-center justify-content-center`}
                                                                    >
                                                                        <span className="fs-12 fw-semibold">
                                                                            {c.initials}
                                                                        </span>
                                                                    </span>
                                                                    <div className="ms-2">
                                                                        <h6 className="fs-14 mb-0">{c.name}</h6>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className={`badge badge-md badge-soft-${
                                                                        ['teal', 'pink', 'orange', 'success', 'info'][
                                                                            i % 5
                                                                        ]
                                                                    }`}
                                                                >
                                                                    {c.invoice_count}
                                                                </span>
                                                            </td>
                                                            <td>{fmt(c.total_invoiced)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* end bottom row */}
                </div>
                {/* End Content */}

                {/* Footer */}
                <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                    <p className="text-dark mb-0">
                        {new Date().getFullYear()} © <span className="link-primary">Burray Invoice</span>, All Rights Reserved
                    </p>
                </div>
            </div>
        </>
    );
};

export default Stats;
